import { Component, computed, effect, inject, Injector, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { tap, map, Observable } from 'rxjs';

import { TransactionBaseComponent } from '../../economics/transaction-base/transaction-base.component';
import { ShipAssignmentComponent } from '../ship-assignment/ship-assignment.component';

import { FleetService } from '../../services/fleet.service';
import { InformationPopulationService } from '../../services/information-population.service';
import { ShipClassService } from '../../services/ship-class.service';
import { StarSystemService } from '../../services/star-system.service';

import { Fleet } from '../../interfaces/fleet';
import { InformationPopulation } from '../../interfaces/information-population';
import { Ship } from '../../interfaces/ship';
import { ShipClass } from '../../interfaces/ship-class';
import { StarSystem, Planet, Moon } from '../../interfaces/star-system';
import { Transaction } from '../../interfaces/transaction';

@Component({
  selector: 'ism-ship-transactions',
  standalone: false,
  templateUrl: './ship-transaction.component.html'
})
export class ShipTransactionComponent extends TransactionBaseComponent implements OnInit {
  injector = inject(Injector);
  fleetService = inject(FleetService);
  informationPopulationService = inject(InformationPopulationService);
  shipClassService = inject(ShipClassService);
  starSystemService = inject(StarSystemService);

  starSystems: Signal<StarSystem[]> = signal([]);
  starSystemsById: { [key: string]: StarSystem; } = {};
  bodyById: { [key: string]: Planet | Moon; } = {};
  bodiesByStarSystemIdAndHex: { [key: string]: string[]; } = {};
  fleets: WritableSignal<Fleet[]>;
  emptyFleets: Fleet[] = [];
  fleetById: { [key: string]: Fleet; } = {};
  fleetsBySystemHex: { [key: string]: Fleet[]; } = {};
  syBySystemHex: { [key: string]: boolean; } = {};
  shipClasses: Signal<ShipClass[]>;
  shipClassById: { [key: string]: ShipClass; } = {};
  informationPopulations: Signal<InformationPopulation[]>;
  hexesByStarSystemId: { [key: string]: Set<string>; } = {};

  selectedTab = signal<string>("extraPool");
  extraPool = signal<Fleet>(undefined);
  governmentPool = signal<Fleet>(undefined);
  mothballedFleet = signal<Fleet>(undefined);

  override transactionType: string = "shipAction";
  action = "buy";
  // starSystemId: string;

  transactionByShipId: { [key: string]: Transaction; };

  private locateFleet (fleet: Fleet) {
    let key = fleet.starSystemId + fleet.locationHex;
    let fleetsAtSystemHex = (this.fleetsBySystemHex[key] || []);
    fleetsAtSystemHex.push(fleet);
    this.fleetsBySystemHex[key] = fleetsAtSystemHex;
  };

  constructor () {
    super();
    this.shipClasses = toSignal(this.shipClassService.getShipClassesForRaceWithCount$(this.race()).pipe(
      tap(shipClasses => {
        this.shipClassById = shipClasses.reduce(function (scHash, shipClass) {
          scHash[shipClass._id] = shipClass;
          return scHash;
        }, this.shipClassById);
      })
    ));

    let fleetsResource = rxResource({
      loader: (): Observable<Fleet[]> =>
        this.fleetService.getFleetsForRaceId$(this.race()._id).pipe(
          map(fleets => {
            let raceFleets = [];
            fleets.forEach((fleet) => {
              if (fleet.fleetName === "Extra CFN") {
                this.extraPool.set(fleet);
              }
              else if (fleet.fleetName === "Government (Leased)") {
                this.governmentPool.set(fleet);
              }
              else if (fleet.fleetName === "Mothballed") {
                this.mothballedFleet.set(fleet);
              }
              else if (!fleet.turnDeactivated || fleet.turnDeactivated < 0) {
                this.fleetById[fleet._id] = fleet;
                if (fleet.shipIds?.length > 0) {
                  raceFleets.push(fleet);
                }
                else {
                  this.emptyFleets.push(fleet);
                }
                // find or create an array of fleets at this hex and add this fleet
                this.locateFleet(fleet);
              }
            });
            return raceFleets;
          })
        )
    });
    this.fleets = fleetsResource.value;

    this.starSystems = toSignal(
      this.starSystemService.getAllStarSystemsForCampaign(this.campaign()._id).pipe(
        tap(starSystems => {
          starSystems.forEach(starSystem => {
            this.starSystemsById[starSystem._id] = starSystem;
            starSystem.stars.forEach(stars => {
              stars.planets.forEach(planet => {
                let key = starSystem._id + planet.strategicHex;
                this.bodyById[planet._id] = planet;
                this.bodiesByStarSystemIdAndHex[key] = [planet._id];
                planet.moons.forEach(moon => {
                  this.bodyById[moon._id] = moon;
                  this.bodiesByStarSystemIdAndHex[key].push(moon._id);
                });
              });
            });
          });
        })
      )
    );

    this.informationPopulations = toSignal(
      this.informationPopulationService.getInformationPopulationForRaceId(this.race()._id)
    );

    effect(() => {
      let byShipId: { [key: string]: Transaction; } = {} as { [key: string]: Transaction; };
      this.transactionByShipId = this.transactions().reduce((hash, transaction) => {
        hash[transaction.shipId] = transaction;
        return hash;
      }, byShipId);
    });

    effect(() => {
      if (!this.shipClasses() || !this.fleets()) {
        return;
      }
      this.shipClasses().forEach((shipClass) => {
        let isFreighter = shipClass.hullClass.substring(0, 2) === "FT";
        shipClass.certified = isFreighter || Math.random() > 0.5;
      });
      this.fleets().forEach((fleet) => {
        let key = fleet.starSystemId + fleet.locationHex;
        this.syBySystemHex[key] = this.syBySystemHex[key] || false;
        fleet.ships?.forEach((ship) => {
          let shipClass = this.shipClassById[ship.shipClassId];
          fleet.hasSy = fleet.hasSy || shipClass.syCount > 0;
          this.syBySystemHex[key] = this.syBySystemHex[key] || fleet.hasSy;
        });
      });
    });
  };

  override ngOnInit (): void {
    super.ngOnInit();

    effect(() => {
      if (!this.starSystems() || !this.informationPopulations()) {
        return;
      }
      this.informationPopulations().forEach((ip) => {
        let ssid = ip.starSystemId;
        let body: Planet | Moon = this.bodyById[ip.planetId];

        let hexes = this.hexesByStarSystemId[ssid] || new Set<string>();
        if (body.strategicHex != null && body.strategicHex != "") {
          hexes.add(body.strategicHex);
          this.hexesByStarSystemId[ssid] = hexes;
        }
      });
    }, { injector: this.injector });
  };

  setExtraPool () {
    this.selectedTab.set('extraPool');
    this.action = "buy";
  };

  setGovernmentPool () {
    this.selectedTab.set('governmentPool');
    this.action = "vacate";
  };

  setMothballedFleet () {
    this.selectedTab.set('mothballedFleet');
    this.action = "activate";
  };

  setRaceFleets () {
    this.selectedTab.set('raceFleets');
    this.action = "race";
    // this.action = "sell/lease/mothball/move";
  };

  newShipTransaction (shipId: string, action: string): Transaction {
    var newShipTransaction = {
      campaignId: this.race().campaignId,
      raceId: this.race()._id,
      turn: this.session.turnEdit(),
      type: this.transactionType,
      description: action,
      shipId: shipId,
      quantity: 0,
      amount: 0
    };
    return newShipTransaction;
  };

  addNewFleet (newFleet: Fleet) {
    this.fleets.update(f => f.concat(newFleet));
    this.locateFleet(newFleet);
  };

  override processEditResult (result: { [key: string]: any; }) {
    let transaction: Transaction = result['document'] as Transaction;
    let ship = result['ship'] as Ship;
    let starSystemId = result['starSystemId'] as string;
    let locationHex = result['locationHex'] as string;
    let fleetName = result['fleetName'] as string;

    this.localProcessResult(transaction, ship, starSystemId, locationHex, fleetName);
  };

  localProcessResult (transaction: Transaction, ship: Ship, starSystemId?: string, locationHex?: string, fleetName?: string) {
    let shipClass = this.shipClassById[ship.shipClassId];

    if (transaction.fleetId != null) {
      // transaction.fleetId = fleetId;
      this.saveTransaction(transaction);
    }
    else if (starSystemId && locationHex && fleetName) {
      let fleetData: Fleet = this.fleetService.getNewFleet(shipClass.campaignId, shipClass.raceId);
      fleetData.starSystemId = starSystemId;
      fleetData.locationHex = locationHex;
      fleetData.fleetName = fleetName;

      this.session.iAmBusy();
      this.fleetService.saveFleet$(fleetData).pipe(
        tap(newFleet => {
          this.addNewFleet(newFleet);
          transaction.fleetId = newFleet._id;
          this.saveTransaction(transaction);
        })
      ).subscribe(
        () => {
          this.session.setNotifyMessage('New Fleet with Ship Action Saved');
          this.session.iAmNotBusy();
        }
      );
    }
    else {
      throw new Error('Ship Transaction Failed');
    }
  };

  edit (ship: Ship, action: string) {
    let transactionToEdit: Transaction;
    let transaction = this.transactionByShipId[ship._id];
    let fleet;
    if (transaction) {
      transactionToEdit = Object.assign({}, transaction);
      fleet = this.fleetById[transaction.fleetId];
    }
    else {
      transactionToEdit = this.newShipTransaction(ship._id, action);
    }
    let config = this.buildDataDialogConfig("Ship Adjustment", transactionToEdit);
    config.data = Object.assign(config.data, {
      action: action,
      ship: ship,
      shipClass: this.shipClassById[ship.shipClassId],
      fleet: fleet,
      starSystemsById: this.starSystemsById,
      hexesByStarSystemId: this.hexesByStarSystemId,
      fleetsBySystemHex: this.fleetsBySystemHex,
      syBySystemHex: this.syBySystemHex
    });
    this.openTransactionDialog("Ship Adjustment", transactionToEdit, ShipAssignmentComponent, config);
  };

  delete (ship: Ship) {
    let transaction = this.transactionByShipId[ship._id];
    this.openDeleteTransactionDialog("Ship Transaction", transaction);
  };

  buy (ship: Ship) {
    this.edit(ship, "buy");
  };

  vacateLease (ship: Ship) {
    this.edit(ship, "vacate");
  };

  activate (ship: Ship) {
    this.edit(ship, "activate");
  };

  sell (ship: Ship) {
    // this.extraPool
    let transaction = this.newShipTransaction(ship._id, "sell");
    transaction.fleetId = this.extraPool()._id;
    transaction.amount = this.shipClassById[ship.shipClassId].buildCost * 0.9;
    this.localProcessResult(transaction, ship);
  };

  lease (ship: Ship) {
    let transaction = this.newShipTransaction(ship._id, "lease");
    transaction.fleetId = this.governmentPool()._id;
    this.localProcessResult(transaction, ship);
  };

  mothball (ship: Ship) {
    let transaction = this.newShipTransaction(ship._id, "mothball");
    transaction.fleetId = this.mothballedFleet()._id;
    this.localProcessResult(transaction, ship);
  };

  raceEditAction (ship: Ship) {
    let shipTransaction = this.transactionByShipId[ship._id] || undefined;
    if (!shipTransaction) {
      this.edit(ship, shipTransaction.description);
    }
  };

  hexesForStarSystemId (ssid: string) {
    return Array.from(this.hexesByStarSystemId[ssid]).sort();
  };

  // isCertified (ship: Ship): boolean {
  //   // What qualifies as certified?
  //   let shipClass = this.shipClassById[ship.shipClassId];
  //   let isFreighter = shipClass.hullClass.substring(0, 2) === "FT";
  //   return isFreighter || Math.random() > 0.5;
  // };

  // atTerminal (fleet: Fleet): boolean {
  //   return true;
  // };

  // atSYTerminal (fleet: Fleet): boolean {
  //   return true;
  // };
};
